import React from 'react';

import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { getEffectiveChecklistStatus, isRowApproved } from '../../manualOverridePersistence';
import ApprovalActionButton from '../../reportCards/ApprovalActionButton';
import BooleanFlag from '../../reportCards/BooleanFlag';
import EmptyTableRow from '../../reportCards/EmptyTableRow';
import HiddenApprovedFooter from '../../reportCards/HiddenApprovedFooter';
import ReportCard from '../../reportCards/ReportCard';
import RuleStatusCard from '../../reportCards/RuleStatusCard';
import { formatContextDate, formatMoney } from '../../shared/format';
import type { ManualOverrideState } from '../../types';

import SurchargeBlackoutOverlapBlock from './SurchargeBlackoutOverlapBlock';
import SurchargeRuleDetails from './SurchargeRuleDetails';
import type { SurchargeTableRow, SurchargesChecklistRow } from './types';

interface Props {
  expandedRows: Record<string, boolean>;
  flaggedRules: Array<SurchargesChecklistRow>;
  hiddenApprovedCount: number;
  manualOverrides: ManualOverrideState;
  rows: Array<SurchargeTableRow>;
  showAutoApproved: boolean;
  onApprovalChange: (rowId: string) => void;
  onToggleDetails: (rowId: string) => void;
  onUnapprovalChange: (rowId: string) => void;
}

export default function SurchargesReportCard({
  expandedRows,
  flaggedRules,
  hiddenApprovedCount,
  manualOverrides,
  rows,
  showAutoApproved,
  onApprovalChange,
  onToggleDetails,
  onUnapprovalChange,
}: Props) {
  return (
    <ReportCard>
      <TableContainer>
        <Table size="small" sx={{ minWidth: 1100 }}>
          <TableHead>
            <TableRow>
              <TableCell>Surcharge Name</TableCell>
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
              <TableCell>Converted Cost</TableCell>
              <TableCell>Surcharge Price</TableCell>
              <TableCell>Accumulate</TableCell>
              <TableCell>Inside Travel Period</TableCell>
              <TableCell>Blackout Overlap</TableCell>
              <TableCell>Rooms</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <EmptyTableRow colSpan={10} label="No surcharges match this view." />
            ) : (
              rows.map((row) => {
                const approved = isRowApproved(row, manualOverrides);
                const roomsExpanded = !!expandedRows[row.id];

                return (
                  <React.Fragment key={row.id}>
                    <TableRow hover sx={approved ? { bgcolor: 'action.hover', opacity: 0.7 } : undefined}>
                      <TableCell sx={{ minWidth: 200 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {row.name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatContextDate(row.fromDate)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatContextDate(row.toDate)}</TableCell>
                      <TableCell>{formatMoney(row.convertedCost, row.currencyCode)}</TableCell>
                      <TableCell>{formatMoney(row.surchargePrice, row.currencyCode)}</TableCell>
                      <TableCell>
                        <BooleanFlag falseIsGood value={row.accumulate} />
                      </TableCell>
                      <TableCell>
                        {row.insideTravelPeriod === null ? (
                          <Typography variant="body2" color="text.disabled">
                            N/A
                          </Typography>
                        ) : (
                          <BooleanFlag value={row.insideTravelPeriod} />
                        )}
                      </TableCell>
                      <TableCell>
                        {row.blackoutOverlap === null ? (
                          <Typography variant="body2" color="text.disabled">
                            N/A
                          </Typography>
                        ) : (
                          <BooleanFlag falseIsGood value={row.blackoutOverlap} />
                        )}
                      </TableCell>
                      <TableCell>
                        {row.dealOptionNames.length === 0 ? (
                          <Typography variant="body2" color="text.disabled">
                            N/A
                          </Typography>
                        ) : (
                          <Button
                            size="small"
                            variant="text"
                            endIcon={roomsExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            onClick={() => onToggleDetails(row.id)}
                          >
                            {roomsExpanded
                              ? `Hide ${row.dealOptionNames.length} room${row.dealOptionNames.length === 1 ? '' : 's'}`
                              : `View ${row.dealOptionNames.length} room${row.dealOptionNames.length === 1 ? '' : 's'}`}
                          </Button>
                        )}
                      </TableCell>
                      <TableCell>
                        <ApprovalActionButton
                          approved={approved}
                          hidden={row.status === 'blocked'}
                          onApprove={() => onApprovalChange(row.id)}
                          onUnapprove={() => onUnapprovalChange(row.id)}
                        />
                      </TableCell>
                    </TableRow>
                    {row.dealOptionNames.length > 0 && (
                      <TableRow>
                        <TableCell colSpan={10} sx={{ p: 0, borderBottom: roomsExpanded ? undefined : 'none' }}>
                          <Collapse in={roomsExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ bgcolor: 'action.hover', px: 2, py: 1.5 }}>
                              <Typography
                                variant="caption"
                                fontWeight={700}
                                color="text.secondary"
                                sx={{ letterSpacing: '0.08em', display: 'block', mb: 1 }}
                              >
                                APPLIES TO
                              </Typography>
                              <Stack direction="row" flexWrap="wrap" gap={1}>
                                {row.dealOptionNames.map((name) => (
                                  <Chip key={name} label={name} size="small" variant="outlined" />
                                ))}
                              </Stack>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <HiddenApprovedFooter count={hiddenApprovedCount} noun="Surcharge" showAutoApproved={showAutoApproved} />

      <SurchargeBlackoutOverlapBlock rows={rows} />

      {flaggedRules.length > 0 && (
        <Box sx={{ p: 2 }}>
          <Stack spacing={1}>
            {flaggedRules.map((rule) => {
              const ruleApproved = isRowApproved(rule, manualOverrides);
              const canApprove = !ruleApproved && rule.status !== 'blocked';

              return (
                <RuleStatusCard
                  key={rule.id}
                  icon={<FlagOutlinedIcon fontSize="small" />}
                  status={getEffectiveChecklistStatus(rule, manualOverrides)}
                  title={
                    <>
                      {rule.name}:
                      {rule.field === 'Surcharge Coverage' && (
                        <Typography component="span" variant="caption" fontWeight={400} color="text.secondary">
                          {' '}
                          cumulative surcharge days must cover no more than <strong>75%</strong> of the travel period.
                        </Typography>
                      )}
                    </>
                  }
                  action={
                    ruleApproved || canApprove ? (
                      <ApprovalActionButton
                        approved={ruleApproved}
                        onApprove={() => onApprovalChange(rule.id)}
                        onUnapprove={() => onUnapprovalChange(rule.id)}
                      />
                    ) : undefined
                  }
                >
                  <SurchargeRuleDetails rule={rule} />
                </RuleStatusCard>
              );
            })}
          </Stack>
        </Box>
      )}
    </ReportCard>
  );
}
